import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { API_BASE_URL } from '../../const/constants';
import { toast } from 'react-toastify';
import TextEditor from "./TextEditor";
import { BsSearch } from "react-icons/bs";
import {
    isImageFile,
    revokeMultipleImageUrls,
} from '../../utils/ImageUtil';
import { hexToRgba } from '../../utils/designUtils';

const CATEGORY_CODE_OPTIONS = [
    { value: '', label: '선택 안 함' },
    { value: 'ALARM', label: '알림' },
    { value: 'NOTICE', label: '공지' },
    { value: 'SCHEDULE', label: '일정' },
    { value: 'ORG', label: '조직' },
    { value: 'APPROVAL', label: '결재' },
    { value: 'PERSONAL', label: '개인' },
    { value: 'REPORT', label: '보고' },
];

const FREQUENCY_OPTIONS = [
    { value: 'DAY', label: '매일 반복' },
    { value: 'WEEKLY', label: '매주 반복' },
    { value: 'MONTHLY', label: '매월 반복' },
    { value: 'YEARLY', label: '매년 반복' },
];

const DAY_OF_WEEK_OPTIONS = [
    { value: 'MON', label: '월요일' },
    { value: 'TUE', label: '화요일' },
    { value: 'WED', label: '수요일' },
    { value: 'THU', label: '목요일' },
    { value: 'FRI', label: '금요일' },
    { value: 'SAT', label: '토요일' },
    { value: 'SUN', label: '일요일' },
];

const WEEK_OF_MONTH_OPTIONS = [
    { value: '#1', label: '첫째 주' },
    { value: '#2', label: '둘째 주' },
    { value: '#3', label: '셋째 주' },
    { value: '#4', label: '넷째 주' },
    { value: 'L', label: '마지막 주' },
];

const DAY_OF_MONTH_OPTIONS = [
    ...Array.from({ length: 31 }, (_, i) => ({
        value: String(i + 1), label: `${i + 1}일`
    })),
    { value: 'L', label: '말일' }
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1), label: `${i + 1}월`
}));

const generateCronExpression = (settings) => {
    let [hourStr = '9', minuteStr = '0'] = (settings.baseTime || '09:00').split(':');

    const hour = parseInt(hourStr);
    const minute = parseInt(minuteStr);

    // Cron 필드 초기값 (일, 월, 요일)
    let cronDayOfMonth = '*';
    let cronMonth = '*';
    let cronDayOfWeek = '?';

    switch (settings.frequencyType) {
        case 'DAY':
            break;
        case 'WEEKLY':
            // cronDayOfWeek = settings.frequencyDetail || 'MON';
            //cronDayOfMonth = '?';
            break;
        case 'MONTHLY':
            if (settings.basisOf === 'date') {
                cronDayOfMonth = settings.frequencyDetail || '1';
                cronDayOfWeek = '?';
            } else {
                let [day, weeks] = (settings.frequencyDetail || 'MON/#1').split('/');
                cronDayOfMonth = '?';
                cronMonth = '*';
                cronDayOfWeek = day + weeks;
            }

            break;
        case 'YEARLY':
            if (settings.basisOf === 'date') {
                let [month, day] = (settings.frequencyDetail || '1/1').split('/');
                cronMonth = month;
                cronDayOfMonth = day;
                cronDayOfWeek = '?';
            } else {
                let [month, day, weeks] = (settings.frequencyDetail || '1/MON/#1').split('/');
                cronDayOfMonth = '?';
                cronMonth = month;
                cronDayOfWeek = day + weeks;
            }
            break;
        default:
            break;
    }

    // 🚨 최종 Cron 표현식: 분 시 일 월 요일 (5개 필드)
    return `0 ${minute} ${hour} ${cronDayOfMonth} ${cronMonth} ${cronDayOfWeek}`;
};

const generateCronDescription = (settings) => {
    let description = '';

    // baseTime (HH:mm) 분리
    let [hour, minute] = (settings.baseTime || '09:00').split(':');

    const hour24 = parseInt(hour);
    const minuteInt = parseInt(minute);

    let ampm = '';
    let hour12 = hour24;

    if (hour24 >= 12) {
        ampm = '오후';
        if (hour24 > 12) {
            hour12 = hour24 - 12;
        }
    } else {
        ampm = '오전';
        if (hour24 === 0) {
            hour12 = 12; // 0시(자정)는 오전 12시로 표시
        }
    }

    const timeDesc = `${ampm} ${hour12}시 ${minuteInt}분`;

    switch (settings.frequencyType) {
        case 'DAY':
            description = `매일 ${timeDesc}마다 실행`;
            break;
        case 'WEEKLY':
            // console.log('settings.frequencyDetail:', settings.frequencyDetail)
            // console.log('DAY_OF_WEEK_OPTIONS.find(item => item.value === settings.frequencyDetail):', DAY_OF_WEEK_OPTIONS.find(item => item.value === settings.frequencyDetail))
            // const dayOfWeek = DAY_OF_WEEK_OPTIONS.find(item => item.value === settings.frequencyDetail).label || '월요일';
            // description = `매주 ${dayOfWeek} ${timeDesc}마다 실행`;
            break;
        case 'MONTHLY':
            if (settings.basisOf === 'date') {
                const dayOfMonth = settings.frequencyDetail || '1';
                description = `매월 ${dayOfMonth}일 ${timeDesc}마다 실행`;
            } else {
                let [day, week] = (settings.frequencyDetail || 'MON/#1').split('/');
                description = `매월 `
                    + WEEK_OF_MONTH_OPTIONS.find(opt => opt.value === week)?.label + ` `
                    + DAY_OF_WEEK_OPTIONS.find(opt => opt.value === day)?.label
                    + ` ${timeDesc}에 실행`;
            }
            break;
        case 'YEARLY':
            if (settings.basisOf === 'date') {
                let [month, day] = (settings.frequencyDetail || '1/1').split('/');
                description = `매년 ${month}월 ${day}일 ${timeDesc}에 실행`;
            } else {
                let [month, day, week] = (settings.frequencyDetail || '1/MON/#1').split('/');
                description = `매년 ${month}월 `
                    + WEEK_OF_MONTH_OPTIONS.find(opt => opt.value === week)?.label + ` `
                    + DAY_OF_WEEK_OPTIONS.find(opt => opt.value === day)?.label
                    + ` ${timeDesc}에 실행`;
            }

            break;
        default:
            description = '반복 설정 정보가 불완전합니다.';
            break;
    }

    return description;
};

const parseCronToSettings = (push) => {
    const cron = push.cron;
    if (!cron) return { baseTime: '09:00', frequencyType: 'DAY', frequencyDetail: '' };

    // const parts = cron.split(' ');
    // const minute = parts[1].padStart(2, '0');
    // const hour = parts[2].padStart(2, '0');
    // const dayOfMonth = parts[3];
    // const month = parts[4];
    // const dayOfWeek = parts[5];

    // let frequencyType = 'DAY';
    // let frequencyDetail = '';

    // if (dayOfWeek !== '?' && dayOfWeek !== '*') {
    //     frequencyType = 'WEEKLY';
    //     frequencyDetail = dayOfWeek;
    // } else if (month !== '*' && month !== '?') {
    //     frequencyType = 'YEARLY';
    //     frequencyDetail = `${month}/${dayOfMonth}`;
    // } else if (dayOfMonth !== '*' && dayOfMonth !== '?') {
    //     frequencyType = 'MONTHLY';
    //     frequencyDetail = dayOfMonth;
    // }

    const parts = cron.split(' ');
    const minute = parts[1].padStart(2, '0');
    const hour = parts[2].padStart(2, '0');
    const dayOfMonth = parts[3];
    const month = parts[4];
    const dayOfWeek = parts[5];

    let frequencyType = 'DAY';
    let frequencyDetail = '';
    let basisOf = 'date';


    // 주차 기준 반복 여부
    const isWeekOfMonth = dayOfWeek.includes('#') || dayOfWeek.endsWith('L');
    // 연간 주차 기준
    if (isWeekOfMonth && month !== '*' && month !== '?') {
        frequencyType = 'YEARLY';
        basisOf = 'weekday';
        frequencyDetail = `${month}/${dayOfWeek.slice(0, 3)}/${dayOfWeek.slice(3)}`;
        // 월간 주차 기준
    } else if (isWeekOfMonth) {
        frequencyType = 'MONTHLY';
        basisOf = 'weekday';
        frequencyDetail = `${dayOfWeek.slice(0, 3)}/${dayOfWeek.slice(3)}`;
        // 연간 날짜 기준
    } else if (month !== '*' && month !== '?') {
        frequencyType = 'YEARLY';
        frequencyDetail = `${month}/${dayOfMonth}`;
        basisOf = 'date';
        // 월간 날짜 기준
    } else if (dayOfMonth !== '*' && dayOfMonth !== '?') {
        frequencyType = 'MONTHLY';
        frequencyDetail = dayOfMonth;
        basisOf = 'date';
    } else if (push.excludedDays?.length > 0) {
        frequencyType = 'WEEKLY';
    }



    return {
        baseTime: `${hour}:${minute}`,
        frequencyType,
        frequencyDetail,
        basisOf
    };
};

const PURPLE = '#7C6BD9';
const MINT = '#5BC2B2';

const pcInputStyle = {
    height: 42, borderRadius: 10, padding: '0 14px', width: '100%',
    border: '1.5px solid #ececf2', background: 'white',
    fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
    transition: 'border-color 140ms, box-shadow 140ms',
    boxSizing: 'border-box',
};

const pcFocusOn = (e) => {
    e.target.style.borderColor = PURPLE;
    e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`;
};
const pcFocusOff = (e) => {
    e.target.style.borderColor = '#ececf2';
    e.target.style.boxShadow = 'none';
};

function ChevronDown14() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#8b8a99', pointerEvents: 'none' }}>
            <path d="M6 9l6 6 6-6" />
        </svg>
    );
}

function PSection({ title, required, action, children }) {
    return (
        <section style={{ background: 'white', borderRadius: 16, border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)', overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '14px 22px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)', borderBottom: '1px solid #ececf2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {title}
                    {required && <span style={{ color: '#d97757', fontSize: 14 }}>*</span>}
                </h3>
                {action}
            </div>
            <div style={{ padding: '20px 22px' }}>{children}</div>
        </section>
    );
}

function PRow({ label, required, children, hint }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'flex-start', gap: 16, padding: '10px 0', borderBottom: '1px solid #f8f7fc' }}>
            <label style={{ paddingTop: 12, fontSize: 13, color: '#3a3849', fontWeight: 600 }}>
                {label}
                {required && <span style={{ color: '#d97757', marginLeft: 4 }}>*</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {children}
                {hint && <span style={{ fontSize: 11.5, color: '#8b8a99' }}>{hint}</span>}
            </div>
        </div>
    );
}

function PCRadioOpt({ checked, onClick, label, sub, disabled }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 14px', borderRadius: 10,
            background: checked ? hexToRgba(PURPLE, .05) : 'white',
            border: `1.5px solid ${checked ? PURPLE : '#ececf2'}`,
            cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: 'all 140ms', textAlign: 'left',
            opacity: disabled ? 0.65 : 1,
        }}>
            <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, background: 'white', position: 'relative', border: `1.5px solid ${checked ? PURPLE : '#cdcbd8'}` }}>
                {checked && <span style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})` }} />}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: checked ? '#1f1d2e' : '#3a3849' }}>{label}</span>
                {sub && <span style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 2 }}>{sub}</span>}
            </div>
        </button>
    );
}

function PCPhonePreview({ title, body, imgUrl, scheduleType, cronDesc, reservation, resvDt, recipientCount }) {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const modeLabel = scheduleType === true ? '스케줄' : (reservation === true ? '실시간 · 예약' : '실시간 · 즉시');
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #f0eff5', padding: '10px 14px', boxShadow: '0 1px 2px rgba(60,50,120,.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#8b8a99', fontWeight: 600, letterSpacing: '.04em', marginBottom: 6 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: MINT }}>
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                    LIVE PREVIEW
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: PURPLE, background: hexToRgba(PURPLE, .10), padding: '3px 8px', borderRadius: 6 }}>{modeLabel}</span>
                    {scheduleType === true && cronDesc && <span style={{ fontSize: 10.5, fontWeight: 600, color: '#5d5b6e', background: '#f4f3f9', padding: '3px 8px', borderRadius: 6 }}>{cronDesc}</span>}
                    {scheduleType === false && reservation === true && resvDt && <span style={{ fontSize: 10.5, fontWeight: 600, color: '#5d5b6e', background: '#f4f3f9', padding: '3px 8px', borderRadius: 6 }}>{resvDt.replace('T', ' ')}</span>}
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: MINT, background: hexToRgba(MINT, .14), padding: '3px 8px', borderRadius: 6 }}>대상 {recipientCount}명</span>
                </div>
            </div>
            <div style={{ width: 320, height: 600, margin: '0 auto', borderRadius: 42, padding: 10, background: 'linear-gradient(160deg, #1c1a2e 0%, #2a2841 100%)', boxShadow: '0 30px 50px -20px rgba(20,15,40,.45), inset 0 0 0 2px rgba(255,255,255,.06), inset 0 2px 4px rgba(255,255,255,.08)', position: 'relative' }}>
                <span style={{ position: 'absolute', left: -2, top: 90, width: 3, height: 30, background: '#0f0d1c', borderRadius: 2 }} />
                <span style={{ position: 'absolute', left: -2, top: 130, width: 3, height: 50, background: '#0f0d1c', borderRadius: 2 }} />
                <span style={{ position: 'absolute', left: -2, top: 190, width: 3, height: 50, background: '#0f0d1c', borderRadius: 2 }} />
                <span style={{ position: 'absolute', right: -2, top: 140, width: 3, height: 70, background: '#0f0d1c', borderRadius: 2 }} />
                <div style={{ width: '100%', height: '100%', borderRadius: 34, background: 'linear-gradient(180deg, #4a3f7a 0%, #5b8db8 35%, #8db5c8 70%, #c9b5a8 100%)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 26px 0', color: 'white', fontWeight: 600, fontSize: 13, position: 'relative' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{hh}:{mm}</span>
                        <span style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 100, height: 26, borderRadius: 14, background: '#0a0814' }} />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                                <rect x="0" y="6" width="2" height="4" rx=".5" fill="white" />
                                <rect x="3" y="4" width="2" height="6" rx=".5" fill="white" />
                                <rect x="6" y="2" width="2" height="8" rx=".5" fill="white" />
                                <rect x="9" y="0" width="2" height="10" rx=".5" fill="white" />
                            </svg>
                            <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
                                <rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke="white" strokeOpacity=".7" />
                                <rect x="2" y="2" width="14" height="6" rx="1" fill="white" />
                                <rect x="19.5" y="3.5" width="1.5" height="3" rx=".5" fill="white" fillOpacity=".7" />
                            </svg>
                        </span>
                    </div>
                    <div style={{ textAlign: 'center', color: 'white', marginTop: 28, textShadow: '0 1px 2px rgba(0,0,0,.20)' }}>
                        <div style={{ fontSize: 16, fontWeight: 500, opacity: .9 }}>
                            {new Date().toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' }).replace(/(\d+)월 (\d+)일 (.+요일)/, '$1월 $2일 $3')}
                        </div>
                        <div style={{ fontSize: 72, fontWeight: 200, letterSpacing: '-0.03em', lineHeight: 1.0, marginTop: -2 }}>{hh}:{mm}</div>
                    </div>
                    <div style={{ position: 'absolute', bottom: 100, left: 12, right: 12, background: 'rgba(255,255,255,.78)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 16, padding: '11px 12px', boxShadow: '0 4px 14px rgba(0,0,0,.10)', border: '0.5px solid rgba(255,255,255,.7)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <img src="/workitalk_icon_square.png" alt="App Icon" width={38} height={38} style={{ borderRadius: 9, flexShrink: 0, boxShadow: '0 1px 2px rgba(0,0,0,.10)', display: 'block', background: 'white' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1f1d2e', textTransform: 'uppercase', letterSpacing: '.02em' }}>WORKITALK</span>
                                <span style={{ fontSize: 11, color: 'rgba(0,0,0,.45)' }}>지금</span>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1f1d2e', marginTop: 2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis' }}>{title}</div>
                            <div style={{ fontSize: 12, color: 'rgba(0,0,0,.65)', lineHeight: 1.4, marginTop: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{body}</div>
                            {imgUrl && <div style={{ marginTop: 6, height: 80, borderRadius: 8, background: `#e9e7ef url(${imgUrl}) center/cover no-repeat`, border: '1px solid rgba(0,0,0,.05)' }} />}
                        </div>
                    </div>
                    <span style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', width: 110, height: 4, borderRadius: 4, background: 'rgba(255,255,255,.85)' }} />
                </div>
            </div>
            <div style={{ fontSize: 11.5, color: '#8b8a99', textAlign: 'center', lineHeight: 1.5 }}>실제 단말 환경에 따라 표시가 달라질 수 있습니다.</div>
        </div>
    );
}

export default function PushCreate() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useTokenStore();
    const isEditMode = location.pathname === '/push/edit';
    const existingPush = location.state;


    // 수정 모드에서 데이터 로딩 상태 (로딩 중에는 화면을 숨김)
    const [isLoading, setIsLoading] = useState(isEditMode);

    // 푸시 기본 정보
    const [pushData, setPushData] = useState({
        title: '',
        msg: '',
        extData: {
            iconUrl: '',
            imageUrl: '',
            body: ''
        },
        msgType: 'M',
        scheduleType: true,
        resvDt: '',
        cron: '0 0 9 * * ?',
        startDt: '',
        endDt: '',
        regDt: '',
        isEnabled: '',
        organizationCode: '',
        status: '',
        sendUserEmail: '',
        segments: [],
        templateId: null,
        categoryCode: '',

        reservation: true,
        cronDesc: '매일 오전 9시 0분마다 실행',
        excludedDays: [],  // 제외할 요일
        adjustDir: 'NONE', //주말 제외 시 이전/이후 발송 선택값 기본값: NONE 연,월 일자 발송시 FORWARD/BACKWARD
    });

    const [enabledMode, setEnabledMode] = useState(pushData.enabled);
    useEffect(() => {
        setEnabledMode(!!pushData.enabled);
    }, [pushData.enabled]);

    const [isLocked, setIsLocked] = useState(false);

    useEffect(() => {
        setIsLocked(
            enabledMode ||
            (
                !pushData.scheduleType &&
                !!pushData.sigId &&
                !!pushData.locked
            )
        );
    }, [
        enabledMode,
        pushData.scheduleType,
        pushData.sigId,
        pushData.locked
    ]);


    // [추가] 푸시 수정 잠금 상태
    // const [isLocked, setIsLocked] = useState(false);

    const [cronSettings, setCronSettings] = useState({
        baseTime: '09:00', // HH:mm 형식으로 변경
        frequencyType: 'DAY',
        frequencyDetail: '',
        basisOf: 'date',
    });

    const [templateList, setTemplateList] = useState([]);
    const [personnel, setPersonnel] = useState([]);

    // 파일 첨부
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [filePreviewUrls, setFilePreviewUrls] = useState({});

    const [showPersonnelModal, setShowPersonnelModal] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [allWorkmanList, setAllWorkmanList] = useState([]);
    const [selectedPersonnel, setSelectedPersonnel] = useState([]);
    const [templateLoadKey, setTemplateLoadKey] = useState(0);

    // 전체 인원 목록 불러오기 - useCallback으로 감싸기
    const loadWorkmanList = useCallback(async () => {
        try {
            const response = await compatApi.post('/project/findWorkmanList', {
                user: user || '',
                organizationCode: user.currentOrganizationCode
            });
            if (response.ok) {
                const workmanList = response.data || [];

                // 앱 사용자 여부 조회
                if (workmanList.length > 0) {
                    const emails = workmanList.map(p => p.userEmail);
                    try {
                        const deviceResponse = await compatApi.post(`/push/device/find?organizationCode=${encodeURIComponent(user.currentOrganizationCode || '')}`, emails);
                        if (deviceResponse.ok) {
                            const appUserEmails = deviceResponse.data || [];
                            const listWithAppUserFlag = workmanList.map(person => ({
                                ...person,
                                isAppUser: appUserEmails.includes(person.userEmail)
                            }));
                            setAllWorkmanList(listWithAppUserFlag);
                            return;
                        }
                    } catch (deviceError) {
                        console.error('앱 사용자 조회 오류:', deviceError);
                    }
                }
                setAllWorkmanList(workmanList);
            } else {
                console.error('인원 목록 조회 실패:', response.status);
                toast.error('인원 목록을 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('인원 목록 조회 오류:', error);
            toast.error('인원 목록을 불러오는 중 오류가 발생했습니다.');
        }
    }, [user]); // 의존성에 조직 코드가 있다면 추가

    // useEffect(() => {
    //     console.log('[PushCreate] Mounted');
    //     return () => {
    //         console.log('[PushCreate] Unmounted');
    //     };
    // }, []);

    useEffect(() => {
    }, [isLocked]);

    useEffect(() => {
        if (isEditMode && existingPush?.sigId) {
            loadPushData(existingPush.sigId);
        }
    }, [isEditMode, existingPush]);

    // 컴포넌트 언마운트 시 Object URL 정리
    useEffect(() => {
        return () => {
            revokeMultipleImageUrls(filePreviewUrls);
        };
    }, [filePreviewUrls]);

    useEffect(() => {
        loadWorkmanList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // 마운트 시 한 번만 호출

    useEffect(() => {
        const loadTemplateList = async () => {
            try {
                const res = await compatApi.get('/template/find/list?organizationCode=' + user?.currentOrganizationCode);
                if (res.ok) {
                    //const resData = res.data.data;
                    //console.log('템플릿 목록 조회 성공:', resData);
                    setTemplateList(res.data.data || []);
                }
            } catch (error) {
                console.error("템플릿 로드 실패:", error);
            }
        };

        loadTemplateList();
    }, [user?.currentOrganizationCode]);

    const loadPushData = async (id) => {
        try {
            let newCronValue = '';
            let newCronDesc = '';
            setIsLoading(true);
            const response = await compatApi.get('/scheduler/find/' + id);

            if (response.ok && response.data.data) {
                const push = response.data.data;
                push.extData = push.extData ? JSON.parse(push.extData) : {};
                // [추가] isLocked 상태 설정
                // setIsLocked(push.isLocked);
                // 1. Cron 설정 역해석 및 적용 (추가된 부분)
                if (push.cron) {
                    const savedSettings = parseCronToSettings(push);
                    setCronSettings(savedSettings);

                    newCronValue = generateCronExpression(savedSettings);
                    newCronDesc = generateCronDescription(savedSettings);
                }
                try {
                    const res = await compatApi.get(`/segment/find/${push.sigId}`);
                    if (res.ok) {
                        const resData = res.data.data;
                        //console.log(`${push.title} 대상자 목록 조회 성공:`, resData);

                        const uniquePersonnelMap = new Map();
                        resData.forEach(item => {
                            if (item.userEmail) {
                                uniquePersonnelMap.set(item.userEmail, item);
                            }
                        });
                        const uniquePersonnelList = Array.from(uniquePersonnelMap.values());

                        const listWithUserNames = await Promise.all(
                            uniquePersonnelList.map(async (person) => {
                                try {
                                    const nameRes = await compatApi.get(`/user/selectUserName?userEmail=${person.userEmail}`);
                                    if (nameRes.ok) {
                                        // console.log(`${person.userEmail} 이름 조회 성공:`, nameRes.data);
                                        return { ...person, userName: nameRes.data };
                                    }
                                } catch (e) {
                                    console.error(`${person.userEmail}의 이름을 가져오는데 실패했습니다.`, e);
                                }
                                return { ...person, userName: '' };
                            })
                        );

                        setPersonnel(listWithUserNames);

                        const personnelSegments = listWithUserNames.map(item => ({ userEmail: item.userEmail }));
                        setPushData({
                            ...push,
                            reservation: !!push.resvDt,
                            segments: personnelSegments || [],
                            organizationCodes: [],
                            excludedDays: push.excludedDays || [],
                            cron: newCronValue,
                            cronDesc: newCronDesc
                        });

                        // 서버에서 가져온 이미지 URL이 있으면 미리보기용으로 설정
                        if (push.extData?.imageUrl) {
                            // 상대 경로를 서버 절대 경로로 변환
                            const fullImageUrl = push.extData.imageUrl.startsWith('http')
                                ? push.extData.imageUrl
                                : `${API_BASE_URL.replace('/api', '')}${push.extData.imageUrl}`;
                            setFilePreviewUrls({ 'server_image': fullImageUrl });
                        }
                    }
                } catch (segmentError) {
                    console.error("대상자 상세 정보 로드 실패:", segmentError);
                }
            } else {
                toast.error('푸시 정보를 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('푸시 정보 로드 오류:', error);
            toast.error('푸시 정보를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const setPreviewUrls = (url) => {
        if (!url) {
            setFilePreviewUrls(prev => {
                const { server_image, ...rest } = prev;
                return rest;
            });
            return;
        }
        const fullImageUrl = url.startsWith('http') ? url : `${API_BASE_URL.replace('/api', '')}${url}`;
        setFilePreviewUrls({ 'server_image': fullImageUrl });
    };

    const handlePushInfoChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;

        // scheduleType인 경우 문자열 "true"/"false"를 실제 boolean으로 변환
        if (name === 'scheduleType' || name === 'reservation') {
            finalValue = value === 'true';
        }
        if (name === 'basisOf') {
            setCronSettings(prev => {
                return {
                    ...prev,
                    basisOf: finalValue
                };
            });

            if (cronSettings.frequencyType === 'YEARLY') {
                if (finalValue === 'date') {
                    cronSettings.frequencyDetail = '1/1';
                    cronSettings.basisOf = 'date'
                } else {
                    cronSettings.frequencyDetail = '1/MON/#1';
                    cronSettings.basisOf = 'weekday'
                }
            }
            if (cronSettings.frequencyType === 'MONTHLY') {
                if (finalValue === 'date') {
                    cronSettings.frequencyDetail = '1';
                    cronSettings.basisOf = 'date'
                } else {
                    cronSettings.frequencyDetail = 'MON/#1';
                    cronSettings.basisOf = 'weekday'
                }
            }


        }


        const newCronValue = generateCronExpression(cronSettings);
        const newCronDesc = generateCronDescription(cronSettings);

        setPushData(prev => {
            const nextData = {
                ...prev,
                [name]: finalValue,
                cron: newCronValue,
                cronDesc: newCronDesc,

                ...(name === 'basisOf' ? { excludedDays: [] } : {}),
                ...(name === 'basisOf' && value !== 'date' ? { adjustDir: 'NONE' } : {}),
                ...(name === 'adjustDir' ? { adjustDir: value } : {})
            };
            if (name.startsWith('cron') || name === 'scheduleType') {
                nextData.cron = generateCronExpression(nextData);
                cronSettings.frequencyType = 'DAY'
            }

            return nextData;
        });
    };

    const handlePushExtData = (field, value) => {
        // imageUrl 필드에 base64 형식 제한 (toast는 TextEditor에서 표시)
        if (field === 'imageUrl' && value && value.startsWith('data:')) {
            return; // base64는 무시
        }
        setPushData(prev => ({
            ...prev,
            extData: {
                ...prev.extData,
                [field]: value
            }
        }));
    };

    const handleCronSettingChange = (e) => {
        const { name, value } = e.target;
        const nextCronSettings = {
            ...cronSettings,
            [name]: value
        };

        if (name === 'frequencyType') {
            // frequencyDetail을 빈 문자열로 초기화하여 이전 값을 제거
            nextCronSettings.frequencyDetail = '';

            // 새로운 타입에 맞는 기본값 설정 (선택이 필요한 경우)
            switch (value) {
                case 'WEEKLY':
                    // 매주 기본값은 이제 없음. 요일 선택으로 변경
                    nextCronSettings.frequencyDetail = '';
                    break;
                case 'MONTHLY':
                    // 매월로 변경 시, 일자 선택 Select Box에 '1'(1일)이 기본값으로 보이도록 설정
                    nextCronSettings.frequencyDetail = '1';
                    nextCronSettings.basisOf = 'date';
                    break;
                case 'YEARLY':
                    // 매년으로 변경 시, '1/1' (1월 1일)을 기본으로 설정
                    nextCronSettings.frequencyDetail = '1/1';
                    nextCronSettings.basisOf = 'date';
                    break;
                case 'DAY':
                default:
                    // 매일은 상세 설정이 필요 없으므로 빈 문자열 유지
                    break;
            }
        }

        if (cronSettings.basisOf === 'date') {
            // 2. YEARLY 모드에서 월(selectedMonth) 또는 일(selectedDay) 선택 시 조합
            if (cronSettings.frequencyType === 'YEARLY' && (name === 'selectedMonth' || name === 'selectedDay')) {
                const currentDetail = cronSettings.frequencyDetail || '1/1';
                let [m, d] = currentDetail.split('/');

                if (name === 'selectedMonth') m = value;
                if (name === 'selectedDay') d = value;

                nextCronSettings.frequencyDetail = `${m}/${d}`;
            }
        } else {
            if (cronSettings.frequencyType === 'YEARLY' && (name === 'selectedMonth' || name === 'selectedWeek' || name === 'selectedDayOfWeek')) {
                const currentDetail = cronSettings.frequencyDetail || '1/MON/#1';
                let [m, d, w] = currentDetail.split('/');

                if (name === 'selectedMonth') m = value;
                if (name === 'selectedWeek') w = value;
                if (name === 'selectedDayOfWeek') d = value;

                nextCronSettings.frequencyDetail = `${m}/${d}/${w}`;
            }

            if (cronSettings.frequencyType === 'MONTHLY' && (name === 'selectedWeek' || name === 'selectedDayOfWeek')) {
                const currentDetail = cronSettings.frequencyDetail || 'MON/#1';
                let [d, w] = currentDetail.split('/');

                if (name === 'selectedDayOfWeek') d = value;
                if (name === 'selectedWeek') w = value;

                nextCronSettings.frequencyDetail = `${d}/${w}`;
            }
        }

        setCronSettings(nextCronSettings);

        // 2. 업데이트된 cronSettings 기반으로 pushData.cron 업데이트
        const newCronValue = generateCronExpression(nextCronSettings);
        const newCronDesc = generateCronDescription(nextCronSettings);

        setPushData(prev => ({
            ...prev,
            cron: newCronValue,
            cronDesc: newCronDesc,
            // 매주 반복이 아닐 때 요일 초기화
            ...(name === 'frequencyType' ? { excludedDays: [] } : {}),

            // MONTHLY 또는 YEARLY일 때 adjustDir 기본값 세팅
            ...(name === 'frequencyType' && {
                adjustDir:
                    value === 'MONTHLY' || value === 'YEARLY'
                        ? 'FORWARD'
                        : 'NONE'
            })
        }));
    }

    const handleExcludedDaysChange = (dayValue) => {
        setPushData(prev => {
            const currentExcluded = prev.excludedDays || [];
            const isExcluded = currentExcluded.includes(dayValue);

            return {
                ...prev,
                excludedDays: isExcluded
                    ? currentExcluded.filter(d => d !== dayValue)
                    : [...currentExcluded, dayValue]
            };
        });
    };

    const handleExcludedDaysWeekendChange = () => {
        setPushData(prev => {
            const currentExcluded = prev.excludedDays || [];

            const hasSat = currentExcluded.includes('SAT');
            const hasSun = currentExcluded.includes('SUN');
            const isWeekendExcluded = hasSat && hasSun;

            return {
                ...prev,
                excludedDays: isWeekendExcluded
                    ? currentExcluded.filter(d => d !== 'SAT' && d !== 'SUN')
                    : [...new Set([...currentExcluded, 'SAT', 'SUN'])]
            };
        });
    };

    const loadTemplateData = async (templateId) => {

        try {
            const response = await compatApi.get('/template/find/' + templateId + '?organizationCode=' + user.currentOrganizationCode);
            const template = response.data.data;


            const parsedExtData = template.extData ? JSON.parse(template.extData) : {};

            setPushData(prev => {
                return {
                    ...prev,
                    msg: template.msg || '',
                    extData: parsedExtData,
                    templateId: template.id
                };
            });

            // After setting data, handle the image preview
            if (parsedExtData.imageUrl) {
                setPreviewUrls(parsedExtData.imageUrl);
            } else {
                // If template has no image, clear the preview
                setFilePreviewUrls({});
            }

            // 템플릿 로드 시 key 변경하여 TextEditor 재마운트
            setTemplateLoadKey(prev => prev + 1);

        } catch (error) {
            console.log('템플릿 정보 로드 오류:', error);
            toast.error('템플릿 정보를 불러오는 중 오류가 발생했습니다.');
        }
    };

    // 검색어에 따라 필터링된 인원 목록 (이미 등록된 인원 제외)
    const getFilteredWorkmanList = () => {
        // 이미 등록된 인원의 이메일 목록
        const registeredEmails = personnel.map(p => p.userEmail);

        // 등록되지 않은 인원만 필터링
        const availableList = allWorkmanList.filter(person =>
            !registeredEmails.includes(person.userEmail)
        );

        // 검색어로 필터링
        if (!searchKeyword.trim()) {
            return availableList;
        }

        return availableList.filter(person =>
            person.userName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
            person.userEmail?.toLowerCase().includes(searchKeyword.toLowerCase())
        );
    };

    // 체크박스 토글
    const handleTogglePersonnel = (person) => {
        setSelectedPersonnel(prev => {
            const exists = prev.find(p => p.userEmail === person.userEmail);
            if (exists) {
                return prev.filter(p => p.userEmail !== person.userEmail);
            } else {
                return [...prev, person];
            }
        });
    };

    // 모달에서 완료 버튼 클릭
    const handleAddPersonnelComplete = () => {
        const newPersonnel = selectedPersonnel.map(person => ({
            userName: person.userName,
            userEmail: person.userEmail,
        }));

        setPersonnel(prev => [...prev, ...newPersonnel]);


        setPushData(prev => {
            // 1. 기존 segments 가져오기 (없으면 빈 배열)
            const existingSegments = prev.segments || [];

            // 2. 새 인원을 객체 리스트 [{ userEmail: "..." }] 형태로 변환
            const newSegments = newPersonnel.map(p => ({
                userEmail: p.userEmail
            }));

            // 3. 두 배열을 합친 후 userEmail 기준으로 중복 제거
            const combined = [...existingSegments, ...newSegments];
            const uniqueSegments = combined.filter((item, index, self) =>
                index === self.findIndex((t) => t.userEmail === item.userEmail)
            );

            return {
                ...prev,
                segments: uniqueSegments
            };
        });
        setShowPersonnelModal(false);
        setSearchKeyword('');
        setSelectedPersonnel([]);
        handleClosePersonnelModal();
    };

    // 모달 열기
    const handleOpenPersonnelModal = () => {
        setShowPersonnelModal(true);
    };

    // 모달 닫기
    const handleClosePersonnelModal = () => {
        setShowPersonnelModal(false);
        setSearchKeyword('');
        //setAllWorkmanList([]);
        setSelectedPersonnel([]);
    };

    const handleRemovePersonnel = (index) => {
        const targetEmail = personnel[index].userEmail;

        setPersonnel(prev => prev.filter((_, i) => i !== index));
        setPushData(prev => ({
            ...prev,
            segments: (prev.segments || []).filter(s => s.userEmail !== targetEmail)
        }));
    };

    const handleSave = async () => {
        // if (!isMaster) {
        //     toast.error('MASTER 권한이 필요합니다.');
        //     return;
        // }

        try {
            if (!pushData.title.trim()) {
                toast.error('발송 목록 명을 입력해주세요.');
                return;
            }

            if (!pushData.msg?.trim()) {
                toast.error('푸시 제목을 입력해주세요.');
                return;
            }

            if (!pushData.extData?.body?.trim()) {
                toast.error('푸시 내용을 입력해주세요.');
                return;
            }


            if (!pushData.segments || pushData.segments.length === 0) {
                toast.error('대상자를 1명 이상 추가해주세요.');
                return;
            }

            if (pushData.reservation) {
                if (!pushData.scheduleType && !pushData.resvDt) {
                    toast.error('발송 예약 일시를 입력해주세요.');
                    return;
                }


                // if (resvTime <= now) {
                //     toast.error("발송 예약 일시는 현재 시각 이후여야 합니다.");
                //     return;
                // }
            }

            // 즉시 발송 여부 판단: 예약 발송이 아니고 스케줄 타입도 아닌 경우
            const isImmediateSend = !pushData.reservation && !pushData.scheduleType;

            const requestData = {
                ...pushData,
                pushType: pushData.msgType,
                repeatType: !pushData.scheduleType ? 'ONE_TIME' : cronSettings.frequencyType || '',
                organizationCode: user.currentOrganizationCode,
                extData: JSON.stringify(pushData.extData),
                // 즉시 발송일 경우 예약 시간을 null로 설정
                resvDt: pushData.reservation ? pushData.resvDt : null,
                // 실시간 발송(1회성)인 경우 cron을 null로 보내어 백엔드가 resvDt로부터 생성하도록 함
                cron: pushData.scheduleType ? pushData.cron : null,
                // 제외 요일 정보 전송
                excludedDays: pushData.excludedDays || [],
            };

            const response = isEditMode ? await compatApi.put('/scheduler/update', requestData) : await compatApi.post('/scheduler/create', requestData);

            if (response.ok) {
                // 신규 생성 모드에서 파일이 있으면 업로드
                if (!isEditMode && attachedFiles.length > 0) {
                    const savedPushId = response.data.data.sigId;
                    try {
                        const fileObj = attachedFiles[0]; // 이미지는 1개
                        const formData = new FormData();
                        formData.append('file', fileObj.file);
                        formData.append('sigId', savedPushId);

                        const uploadResponse = await compatApi.upload('/file/upload/notification', formData);

                        if (uploadResponse.ok) {
                            const uploadedImageUrl = uploadResponse.data;

                            // 업로드된 이미지 URL을 DB에 저장하기 위해 extData만 업데이트
                            const extData = JSON.stringify({
                                ...pushData.extData,
                                imageUrl: uploadedImageUrl
                            })

                            const extUpdateResponse = await compatApi.put(
                                `/scheduler/update/extData/${savedPushId}`,
                                extData,
                                { 'Content-Type': 'text/plain' }
                            );

                            if (extUpdateResponse.ok) {
                                console.log('extData 업데이트 성공');
                            } else {
                                toast.warning('푸시는 저장되었으나 이미지 경로 업데이트에 실패했습니다.');
                            }
                        }
                    } catch (error) {
                        console.error('이미지 업로드 오류:', error);
                        toast.warning('푸시는 저장되었으나 이미지 업로드 중 오류가 발생했습니다.');
                    }
                }

                // 즉시 발송인 경우 트리거 API 호출
                if (!isEditMode && isImmediateSend) {
                    const savedPushId = response.data.data.sigId;
                    try {
                        const triggerResponse = await compatApi.post(`/scheduler/trigger/${savedPushId}`);
                        if (triggerResponse.ok) {
                            toast.success('푸시가 즉시 발송되었습니다.');
                        } else {
                            toast.warning('푸시는 저장되었으나 발송에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('푸시 발송 트리거 오류:', error);
                        toast.warning('푸시는 저장되었으나 발송 중 오류가 발생했습니다.');
                    }
                } else {
                    toast.success(isEditMode ? '푸시 발송 정보가 수정되었습니다.' : '푸시 발송 정보가 생성되었습니다.');
                }

                if (isEditMode) {
                    window.location.reload();
                    return {
                        success: true
                    };
                } else {
                    navigate('/push/list');
                }
            } else {
                toast.error(isEditMode ? '푸시 발송 정보 수정에 실패했습니다.' : '푸시 발송 정보 생성에 실패했습니다.');
                return { success: false };
            }
        } catch (error) {
            console.error('푸시 발송 정보 저장 오류:', error);
            toast.error('푸시 발송 정보 저장 중 오류가 발생했습니다.');
            return { success: false };
        }
    };

    const handleCancel = () => {
        if (window.confirm('작성 중인 내용이 저장되지 않습니다. 취소하시겠습니까?')) {
            navigate('/push/list');
        }
    };

    const handleDelete = async () => {
        // if (!isMaster) {
        //     toast.error('MASTER 권한이 필요합니다.');
        //     return;
        // }

        if (!isEditMode || !pushData.sigId) {
            toast.error('삭제할 푸시 발송 정보가 없습니다.');
            return;
        }

        if (!window.confirm('정말 이 푸시 발송 정보를 삭제하시겠습니까?')) {
            return;
        }

        try {
            const response = await compatApi.del('/scheduler/delete/' + pushData.sigId, null);

            if (response.ok) {
                toast.success(pushData.title + ' 푸시 발송 정보가 삭제되었습니다.');
                navigate('/push/list');
            } else {
                toast.error('푸시 발송 정보 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('푸시 발송 정보 삭제 오류:', error);
            toast.error('푸시 발송 정보 삭제 중 오류가 발생했습니다.');
        }
    };

    const handleStartSchedule = async () => {

        const result = await handleSave();

        if (!result?.success) {
            return
        }

        // if (!isMaster) {
        //     toast.error('MASTER 권한이 필요합니다.');
        //     return;
        // }

        if (!isEditMode || !pushData.sigId) {
            toast.error('실행할 푸시 발송 정보가 없습니다.');
            return;
        }

        if (!window.confirm('정말 이 스케줄 발송을 실행하시겠습니까?')) {
            return;
        }

        try {
            const response = await compatApi.put('/scheduler/update/enabled', {
                sigId: pushData.sigId,
                enabled: true,
                organizationCode: pushData.organizationCode
            });

            if (response.ok) {
                toast.success(pushData.title + ' 스케줄 발송이 실행되었습니다.');
                //navigate('/push/list');               
                setEnabledMode(true);
                setIsLocked(true);
            } else {
                toast.error('스케줄 발송 실행에 실패했습니다.');
            }
        } catch (error) {
            console.error('스케줄 발송 실행 오류:', error);
            toast.error('스케줄 발송 실행 중 오류가 발생했습니다.');
        }
    };

    const handleStopSchedule = async () => {
        // if (!isMaster) {
        //     toast.error('MASTER 권한이 필요합니다.');
        //     return;
        // }

        if (!isEditMode || !pushData.sigId) {
            toast.error('중단할 푸시 발송 정보가 없습니다.');
            return;
        }

        if (!window.confirm('정말 이 스케줄 발송을 중단하시겠습니까?')) {
            return;
        }

        try {
            const response = await compatApi.put('/scheduler/update/enabled', {
                sigId: pushData.sigId,
                enabled: false,
                organizationCode: pushData.organizationCode
            });

            if (response.ok) {
                toast.success(pushData.title + ' 스케줄 발송이 중단되었습니다.');
                //navigate('/push/list');
                setEnabledMode(false);
                setIsLocked(false);
            } else {
                toast.error('스케줄 발송 중단에 실패했습니다.');
            }
        } catch (error) {
            console.error('스케줄 발송 중단 오류:', error);
            toast.error('스케줄 발송 중단 중 오류가 발생했습니다.');
        }
    };

    const handleGoToList = () => {
        navigate('/push/list');
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // 이미지 파일인지 확인
        if (!isImageFile(file.name)) {
            toast.error('이미지 파일만 업로드 가능합니다.');
            e.target.value = null;
            return;
        }

        // 파일 크기 체크 (1MB)
        const maxSize = 1 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error('파일 크기는 1MB를 초과할 수 없습니다.');
            e.target.value = null;
            return;
        }

        // 수정 모드일 때: 즉시 업로드
        if (isEditMode && pushData.sigId) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('sigId', pushData.sigId);
                const response = await compatApi.upload('/file/upload/notification', formData);

                if (response.ok) {
                    const uploadFilepath = response.data;
                    handlePushExtData('imageUrl', uploadFilepath);

                    // 미리보기 URL 업데이트
                    const fullImageUrl = uploadFilepath.startsWith('http')
                        ? uploadFilepath
                        : `${API_BASE_URL.replace('/api', '')}${uploadFilepath}`;
                    setFilePreviewUrls({ 'server_image': fullImageUrl });

                    toast.success('이미지가 업로드되었습니다.');
                } else {
                    toast.error('이미지 업로드에 실패했습니다.');
                }
            } catch (error) {
                console.error('이미지 업로드 실패:', error);
                toast.error('이미지 업로드에 실패했습니다.');
            }
        } else {
            // 생성 모드: 임시 저장
            const timestamp = Date.now();
            const fileObj = {
                file,
                tempId: `temp_${timestamp}`
            };
            setAttachedFiles([fileObj]); // 이미지는 1개만

            // 로컬 미리보기 생성
            const url = URL.createObjectURL(file);
            const tempUrl = { [`temp_${timestamp}`]: url };
            setFilePreviewUrls(tempUrl);

            // extData에는 파일명만 설정 (blob URL 대신)
            handlePushExtData('imageUrl', file.name);
            toast.success('이미지가 임시 저장되었습니다. 푸시 저장 시 업로드됩니다.');
        }

        e.target.value = null;
    }

    const getPlainText = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };


    const previewImageUrl = (() => {
        if (attachedFiles.length > 0 && filePreviewUrls[attachedFiles[0].tempId]) {
            return filePreviewUrls[attachedFiles[0].tempId];
        }
        if (filePreviewUrls['server_image']) return filePreviewUrls['server_image'];
        const serverImg = pushData.extData?.imageUrl;
        if (serverImg && serverImg.startsWith('http')) return serverImg;
        return null;
    })();

    if (isLoading) {
        return (
            <div style={{ background: '#f5f4fa', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)', paddingBottom: 80 }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* Page header */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>푸시</span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}>
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>{isEditMode ? '푸시 수정' : '푸시 생성'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>
                                {isEditMode ? '푸시 수정' : '푸시 생성'}
                            </h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>
                                일정·대상·메시지를 한 화면에서 구성하고 실시간으로 미리 확인하세요.
                            </p>
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'white', padding: '8px 14px', borderRadius: 12, border: '1px solid #ececf2', fontSize: 12, color: '#5d5b6e', boxShadow: '0 1px 2px rgba(60,50,120,.04)', flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: PURPLE, display: 'inline-block' }} />기본 정보
                            </span>
                            <span style={{ color: '#d4d3df' }}>→</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: PURPLE, display: 'inline-block' }} />대상자
                            </span>
                            <span style={{ color: '#d4d3df' }}>→</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: MINT, display: 'inline-block' }} />
                                <b style={{ color: '#1f1d2e' }}>메시지 구성</b>
                            </span>
                        </div>
                    </div>
                </div>

                {/* Two-column layout */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 20, alignItems: 'flex-start' }}>
                    <div>
                        {/* 기본 정보 */}
                        <PSection title={isEditMode ? '푸시 수정' : '푸시 기본 정보'}>
                            <PRow label="발송 목록 명" required>
                                <input type="text" name="title" value={pushData.title} onChange={handlePushInfoChange} placeholder="발송 목록 명을 입력하세요" disabled={isLocked} style={pcInputStyle} onFocus={pcFocusOn} onBlur={pcFocusOff} />
                            </PRow>

                            {user.currentRole === 'ADMIN' && (
                                <PRow label="메시지 타입" required>
                                    <div style={{ display: 'flex', gap: 16, paddingTop: 10 }}>
                                        {[{ value: 'P', label: 'public' }, { value: 'M', label: 'private' }, { value: 'N', label: 'notification' }].map(opt => (
                                            <label key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: isLocked ? 'not-allowed' : 'pointer', fontSize: 13, color: '#3a3849' }}>
                                                <input type="radio" name="msgType" value={opt.value} checked={pushData.msgType === opt.value} onChange={handlePushInfoChange} disabled={isLocked} />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </PRow>
                            )}
                            {user.currentRole !== 'ADMIN' && (
                                <input type="hidden" name="msgType" value={pushData.msgType || 'P'} />
                            )}

                            <PRow label="카테고리 코드">
                                <div style={{ position: 'relative' }}>
                                    <select name="categoryCode" value={pushData.categoryCode || ''} onChange={handlePushInfoChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36, cursor: isLocked ? 'not-allowed' : 'pointer' }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                        {CATEGORY_CODE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                    <ChevronDown14 />
                                </div>
                            </PRow>

                            <PRow label="반복 여부" required>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <PCRadioOpt checked={pushData.scheduleType === true} onClick={() => !isLocked && handlePushInfoChange({ target: { name: 'scheduleType', value: 'true' } })} label="스케줄 발송" sub="지정 시각에 반복 자동 발송" disabled={isLocked} />
                                    <PCRadioOpt checked={pushData.scheduleType === false} onClick={() => !isLocked && handlePushInfoChange({ target: { name: 'scheduleType', value: 'false' } })} label="실시간 발송" sub="저장 즉시 1회 전송" disabled={isLocked} />
                                </div>
                            </PRow>

                            {pushData.scheduleType === true && (
                                <PRow label="반복 주기" required>
                                    <div style={{ padding: 16, borderRadius: 12, background: '#fafaff', border: '1px solid #ececf2' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                <span style={{ fontSize: 12, color: '#5d5b6e', fontWeight: 500 }}>반복 주기</span>
                                                <div style={{ position: 'relative' }}>
                                                    <select name="frequencyType" value={cronSettings.frequencyType} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36, cursor: isLocked ? 'not-allowed' : 'pointer' }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                        {FREQUENCY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                    </select>
                                                    <ChevronDown14 />
                                                </div>
                                            </div>
                                            {(cronSettings.frequencyType === 'DAY' || cronSettings.frequencyType === 'WEEKLY') && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    <span style={{ fontSize: 12, color: '#5d5b6e', fontWeight: 500 }}>기준 시각 (HH:mm)</span>
                                                    <input type="time" name="baseTime" value={cronSettings.baseTime} onChange={handleCronSettingChange} disabled={isLocked} style={pcInputStyle} onFocus={pcFocusOn} onBlur={pcFocusOff} />
                                                </div>
                                            )}
                                        </div>

                                        {(cronSettings.frequencyType === 'MONTHLY' || cronSettings.frequencyType === 'YEARLY') && (
                                            <div style={{ marginTop: 16 }}>
                                                <div style={{ marginBottom: 12 }}>
                                                    <span style={{ fontSize: 12, color: '#5d5b6e', fontWeight: 500, display: 'block', marginBottom: 8 }}>반복 기준</span>
                                                    <div style={{ display: 'flex', gap: 16 }}>
                                                        {[{ value: 'date', label: '일자 기준' }, { value: 'weekday', label: '요일 기준' }].map(opt => (
                                                            <label key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                                                                <input type="radio" name="basisOf" value={opt.value} checked={cronSettings.basisOf === opt.value} onChange={handlePushInfoChange} disabled={isLocked} />
                                                                {opt.label}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>

                                                {cronSettings.frequencyType !== 'WEEKLY' && (
                                                    <div style={{ marginBottom: 12 }}>
                                                        <span style={{ fontSize: 12, color: '#5d5b6e', fontWeight: 500, display: 'block', marginBottom: 8 }}>
                                                            {cronSettings.frequencyType === 'MONTHLY'
                                                                ? (cronSettings.basisOf === 'date' ? '일자 선택' : '주차/요일 선택')
                                                                : (cronSettings.basisOf === 'date' ? '월/일 입력' : '월/주차/요일 선택')}
                                                        </span>
                                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                                            {cronSettings.frequencyType === 'MONTHLY' && cronSettings.basisOf === 'date' && (
                                                                <>
                                                                    <div style={{ position: 'relative', flex: 1 }}>
                                                                        <select name="frequencyDetail" value={cronSettings.frequencyDetail} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36 }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                                            {DAY_OF_MONTH_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                        </select>
                                                                        <ChevronDown14 />
                                                                    </div>
                                                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={pushData.excludedDays?.includes('SAT') && pushData.excludedDays?.includes('SUN')} onChange={() => handleExcludedDaysWeekendChange()} disabled={isLocked} />
                                                                        주말 제외
                                                                    </label>
                                                                </>
                                                            )}
                                                            {cronSettings.frequencyType === 'MONTHLY' && cronSettings.basisOf !== 'date' && (
                                                                <>
                                                                    <div style={{ position: 'relative', flex: 1 }}>
                                                                        <select name="selectedWeek" value={cronSettings.frequencyDetail?.split('/')[1] || '#1'} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36 }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                                            {WEEK_OF_MONTH_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                        </select>
                                                                        <ChevronDown14 />
                                                                    </div>
                                                                    <div style={{ position: 'relative', flex: 1 }}>
                                                                        <select name="selectedDayOfWeek" value={cronSettings.frequencyDetail?.split('/')[0] || 'MON'} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36 }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                                            {DAY_OF_WEEK_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                        </select>
                                                                        <ChevronDown14 />
                                                                    </div>
                                                                </>
                                                            )}
                                                            {cronSettings.frequencyType === 'YEARLY' && (
                                                                <>
                                                                    <div style={{ position: 'relative', minWidth: 90 }}>
                                                                        <select name="selectedMonth" value={cronSettings.frequencyDetail?.split('/')[0] || '1'} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36 }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                                            {MONTH_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                        </select>
                                                                        <ChevronDown14 />
                                                                    </div>
                                                                    {cronSettings.basisOf === 'date' ? (
                                                                        <>
                                                                            <div style={{ position: 'relative', flex: 1 }}>
                                                                                <select name="selectedDay" value={cronSettings.frequencyDetail?.split('/')[1] || '1'} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36 }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                                                    {DAY_OF_MONTH_OPTIONS.filter(opt => {
                                                                                        if (opt.value === 'L') return true;
                                                                                        const selectedMonth = parseInt(cronSettings.frequencyDetail?.split('/')[0] || '1');
                                                                                        const maxDays = { 1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30, 7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31 };
                                                                                        return parseInt(opt.value) <= maxDays[selectedMonth];
                                                                                    }).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                                </select>
                                                                                <ChevronDown14 />
                                                                            </div>
                                                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                                                                <input type="checkbox" checked={pushData.excludedDays?.includes('SAT') && pushData.excludedDays?.includes('SUN')} onChange={() => handleExcludedDaysWeekendChange()} disabled={isLocked} />
                                                                                주말 제외
                                                                            </label>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div style={{ position: 'relative', flex: 1 }}>
                                                                                <select name="selectedWeek" value={cronSettings.frequencyDetail?.split('/')[2] || '#1'} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36 }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                                                    {WEEK_OF_MONTH_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                                </select>
                                                                                <ChevronDown14 />
                                                                            </div>
                                                                            <div style={{ position: 'relative', flex: 1 }}>
                                                                                <select name="selectedDayOfWeek" value={cronSettings.frequencyDetail?.split('/')[1]} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, appearance: 'none', paddingRight: 36 }} onFocus={pcFocusOn} onBlur={pcFocusOff}>
                                                                                    {DAY_OF_WEEK_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                                </select>
                                                                                <ChevronDown14 />
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                    {cronSettings.frequencyDetail?.split('/')[0] === '2' && cronSettings.frequencyDetail?.split('/')[1] === '29' && (
                                                                        <span style={{ fontSize: 11.5, color: '#c44569', display: 'block', width: '100%', marginTop: 6 }}>
                                                                            ⚠️ 주의: 2월 29일은 윤년에만 발송되며, 평년에는 발송되지 않습니다.
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {cronSettings.basisOf === 'date' && pushData.excludedDays?.includes('SAT') && pushData.excludedDays?.includes('SUN') && (
                                                    <div style={{ marginBottom: 12 }}>
                                                        <span style={{ fontSize: 12, color: '#5d5b6e', fontWeight: 500, display: 'block', marginBottom: 8 }}>제외일 발송 기준</span>
                                                        <div style={{ display: 'flex', gap: 16 }}>
                                                            {[{ value: 'FORWARD', label: '이후 발송' }, { value: 'BACKWARD', label: '이전 발송' }].map(opt => (
                                                                <label key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                                                                    <input type="radio" name="adjustDir" value={opt.value} checked={pushData.adjustDir === opt.value} onChange={handlePushInfoChange} disabled={isLocked} />
                                                                    {opt.label}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div>
                                                    <span style={{ fontSize: 12, color: '#5d5b6e', fontWeight: 500, display: 'block', marginBottom: 8 }}>기준 시각 (HH:mm)</span>
                                                    <input type="time" name="baseTime" value={cronSettings.baseTime} onChange={handleCronSettingChange} disabled={isLocked} style={{ ...pcInputStyle, width: 'auto', minWidth: 120 }} onFocus={pcFocusOn} onBlur={pcFocusOff} />
                                                </div>
                                            </div>
                                        )}

                                        {cronSettings.frequencyType === 'WEEKLY' && (
                                            <div style={{ marginTop: 16 }}>
                                                <span style={{ fontSize: 12, color: '#5d5b6e', fontWeight: 500, display: 'block', marginBottom: 8 }}>발송 요일</span>
                                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                    {DAY_OF_WEEK_OPTIONS.map(day => (
                                                        <label key={day.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#3a3849' }}>
                                                            <input type="checkbox" checked={!pushData.excludedDays?.includes(day.value)} onChange={() => handleExcludedDaysChange(day.value)} disabled={isLocked} />
                                                            {day.label}
                                                        </label>
                                                    ))}
                                                </div>
                                                <span style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 6, display: 'block' }}>체크 해제한 요일에는 발송되지 않습니다.</span>
                                            </div>
                                        )}

                                        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'white', border: `1px dashed ${hexToRgba(PURPLE, 0.30)}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontSize: 10, fontWeight: 700, color: PURPLE, letterSpacing: '.08em', background: hexToRgba(PURPLE, 0.10), padding: '2px 6px', borderRadius: 5 }}>CRON</span>
                                            <span style={{ fontSize: 12.5, color: '#3a3849' }}>{pushData.cronDesc}</span>
                                            <code style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5, color: '#8b8a99', padding: '2px 8px', background: '#f4f3f9', borderRadius: 5 }}>{pushData.cron}</code>
                                        </div>
                                    </div>
                                </PRow>
                            )}

                            {pushData.scheduleType === false && (
                                <PRow label="예약 발송 여부" required>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                        <PCRadioOpt checked={pushData.reservation === true} onClick={() => !isLocked && handlePushInfoChange({ target: { name: 'reservation', value: 'true' } })} label="예약 발송" sub="지정 일시에 1회 발송" disabled={isLocked} />
                                        <PCRadioOpt checked={pushData.reservation === false} onClick={() => !isLocked && handlePushInfoChange({ target: { name: 'reservation', value: 'false' } })} label="즉시 발송" sub="저장 즉시 1회 전송" disabled={isLocked} />
                                    </div>
                                    {pushData.reservation === true && (
                                        <div style={{ marginTop: 10, padding: 14, borderRadius: 12, background: '#fafaff', border: '1px solid #ececf2', display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'center', gap: 12 }}>
                                            <label style={{ fontSize: 13, color: '#3a3849', fontWeight: 600 }}>발송 예약 일시 <span style={{ color: '#d97757', marginLeft: 2 }}>*</span></label>
                                            <input type="datetime-local" name="resvDt" value={!pushData.reservation ? '' : (pushData.resvDt || '')} onChange={handlePushInfoChange} disabled={!pushData.reservation || isLocked} style={pcInputStyle} onFocus={pcFocusOn} onBlur={pcFocusOff} />
                                        </div>
                                    )}
                                </PRow>
                            )}

                            {pushData.scheduleType && (
                                <PRow label="시작일 / 종료일">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                        <input type="datetime-local" name="startDt" value={pushData.startDt || ''} onChange={handlePushInfoChange} disabled={isLocked} style={pcInputStyle} onFocus={pcFocusOn} onBlur={pcFocusOff} />
                                        <input type="datetime-local" name="endDt" value={pushData.endDt || ''} onChange={handlePushInfoChange} disabled={isLocked} style={pcInputStyle} onFocus={pcFocusOn} onBlur={pcFocusOff} />
                                    </div>
                                </PRow>
                            )}
                        </PSection>

                        {/* 대상자 정보 */}
                        <PSection
                            title="대상자 정보"
                            required
                            action={
                                !isLocked && (
                                    <button onClick={handleOpenPersonnelModal} style={{ height: 30, padding: '0 12px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, boxShadow: `0 4px 10px -4px ${hexToRgba(PURPLE, 0.55)}` }}>
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                                        대상자 추가
                                    </button>
                                )
                            }
                        >
                            {personnel.length === 0 ? (
                                <div style={{ padding: '40px 20px', textAlign: 'center', background: '#fafaff', borderRadius: 12, border: '1px dashed #ececf2' }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, margin: '0 auto 10px', background: hexToRgba(PURPLE, 0.10), color: PURPLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="9" cy="8" r="3.5" /><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" /><path d="M20 8v6M23 11h-6" />
                                        </svg>
                                    </div>
                                    <div style={{ fontSize: 13, color: '#5d5b6e', fontWeight: 500 }}>대상자를 추가해주세요.</div>
                                    <div style={{ fontSize: 11.5, color: '#8b8a99', marginTop: 4 }}>회원 목록에서 선택할 수 있습니다.</div>
                                </div>
                            ) : (
                                <div style={{ borderRadius: 12, border: '1px solid #ececf2', overflow: 'hidden' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: isLocked ? '56px 1fr 1.5fr' : '56px 1fr 1.5fr 60px', background: '#f7f6fb', padding: '10px 12px', fontSize: 11.5, fontWeight: 600, color: '#5d5b6e', letterSpacing: '.02em' }}>
                                        <span style={{ textAlign: 'center' }}>번호</span>
                                        <span>이름</span>
                                        <span>이메일</span>
                                        {!isLocked && <span style={{ textAlign: 'center' }}>삭제</span>}
                                    </div>
                                    {personnel.map((person, index) => (
                                        <div key={index} style={{ display: 'grid', gridTemplateColumns: isLocked ? '56px 1fr 1.5fr' : '56px 1fr 1.5fr 60px', alignItems: 'center', padding: '10px 12px', borderTop: index === 0 ? 'none' : '1px solid #f4f3f9', background: 'white' }}>
                                            <span style={{ textAlign: 'center', fontSize: 12, color: '#8b8a99', fontVariantNumeric: 'tabular-nums' }}>{index + 1}</span>
                                            <span style={{ fontSize: 13, color: '#1f1d2e' }}>{person.userName}</span>
                                            <span style={{ fontSize: 13, color: '#3a3849' }}>{person.userEmail}</span>
                                            {!isLocked && (
                                                <button onClick={() => handleRemovePersonnel(index)} style={{ width: 30, height: 30, borderRadius: 7, margin: '0 auto', background: 'transparent', border: 'none', cursor: 'pointer', color: '#c7c5d2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(196,69,105,.10)'; e.currentTarget.style.color = '#c44569'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#c7c5d2'; }}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </PSection>

                        {/* 템플릿 */}
                        <PSection title="템플릿">
                            <TextEditor
                                key={(pushData.sigId || 'new') + '_' + templateLoadKey}
                                initialContent={pushData.extData}
                                templateList={templateList}
                                title={pushData.msg}
                                onTitleChange={(value) => handlePushInfoChange({ target: { name: 'msg', value } })}
                                onTemplateSelect={loadTemplateData}
                                onExtDataChange={handlePushExtData}
                                handleImageChange={handleImageChange}
                                isLocked={isLocked}
                                imagePreviewUrl={
                                    attachedFiles.length > 0
                                        ? filePreviewUrls[attachedFiles[0].tempId]
                                        : filePreviewUrls['server_image'] || null
                                }
                                setPreviewUrls={setPreviewUrls}
                                templateId={pushData.templateId}
                                onTemplateModified={() => setPushData(prev => ({ ...prev, templateId: null }))}
                                enableTemplateSelect={!isLocked}
                            />
                        </PSection>
                    </div>

                    {/* Right: phone preview */}
                    <div style={{ position: 'sticky', top: 24 }}>
                        <PCPhonePreview
                            title={pushData.msg || '제목을 입력하세요'}
                            body={getPlainText(pushData.extData?.body || '내용 없음')}
                            imgUrl={previewImageUrl}
                            scheduleType={pushData.scheduleType}
                            cronDesc={pushData.cronDesc}
                            reservation={pushData.reservation}
                            resvDt={pushData.resvDt}
                            recipientCount={personnel.length}
                        />
                    </div>
                </div>
            </main>

            {/* Fixed bottom action bar */}
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(255,255,255,.92)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderTop: '1px solid #ececf2', padding: '12px clamp(20px, 3vw, 40px)', display: 'flex', justifyContent: 'flex-end', gap: 8, zIndex: 20 }}>
                <button onClick={handleCancel} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>취소</button>

                {isEditMode && (
                    <button onClick={handleDelete} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'white', border: '1.5px solid #f0c0c0', color: '#c44569', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>삭제</button>
                )}

                {isEditMode && pushData.scheduleType === true && !enabledMode && (
                    <button onClick={handleStartSchedule} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'white', border: `1.5px solid ${MINT}`, color: MINT, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>실행</button>
                )}
                {isEditMode && enabledMode && (
                    <button onClick={handleStopSchedule} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'white', border: '1.5px solid #e8a838', color: '#c78a00', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>발송중단</button>
                )}

                {(!isEditMode || (!enabledMode && !(pushData.scheduleType === false && pushData.sendCount > 0))) && (
                    <button onClick={handleSave} style={{ height: 40, padding: '0 22px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, 0.55)}`, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22 2-7 20-4-9-9-4z" /><path d="M22 2 11 13" />
                        </svg>
                        {pushData.scheduleType === false && pushData.reservation === false ? '즉시 발송' : '예약 등록'}
                    </button>
                )}

                {isEditMode && pushData.scheduleType === false && pushData.sendCount > 0 && !enabledMode && (
                    <button onClick={handleGoToList} style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>목록</button>
                )}
            </div>
            {/* 대상자 추가 모달 */}
            {showPersonnelModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                    onClick={handleClosePersonnelModal}>
                    <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 8px 40px rgba(60,50,120,.20)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
                        onClick={(e) => e.stopPropagation()}>

                        {/* 헤더 */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0eff5', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)', flexShrink: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 32, height: 32, borderRadius: 9, background: hexToRgba(PURPLE, 0.12), color: PURPLE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="9" cy="8" r="3.5" /><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" /><path d="M20 8v6M23 11h-6" />
                                    </svg>
                                </div>
                                <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1f1d2e' }}>푸시 수신 대상자 추가</h5>
                            </div>
                            <button onClick={handleClosePersonnelModal} style={{ width: 30, height: 30, borderRadius: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: '#7a7889', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#ececf2'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* 검색창 */}
                        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f0eff5', flexShrink: 0 }}>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#8b8a99', display: 'flex', pointerEvents: 'none' }}>
                                    <BsSearch size={14} />
                                </span>
                                <input
                                    type="text"
                                    placeholder="이름이나 이메일을 검색하세요"
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    style={{ height: 40, width: '100%', borderRadius: 10, border: '1.5px solid #ececf2', paddingLeft: 36, paddingRight: 14, fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', background: 'white', boxSizing: 'border-box', transition: 'border-color 140ms, box-shadow 140ms' }}
                                    onFocus={(e) => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`; }}
                                    onBlur={(e) => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                                />
                            </div>
                        </div>

                        {/* 목록 */}
                        <div style={{ flex: 1, overflowY: 'auto', minHeight: 240 }}>
                            {getFilteredWorkmanList().length > 0 ? (
                                getFilteredWorkmanList().map((person) => {
                                    const isChecked = selectedPersonnel.some(p => p.userEmail === person.userEmail);
                                    return (
                                        <div
                                            key={person.userEmail}
                                            onClick={() => handleTogglePersonnel(person)}
                                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', cursor: 'pointer', borderBottom: '1px solid #f4f3f9', transition: 'background 100ms', background: isChecked ? hexToRgba(PURPLE, .04) : 'white' }}
                                            onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.background = '#fafaff'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = isChecked ? hexToRgba(PURPLE, .04) : 'white'; }}
                                        >
                                            {/* 커스텀 체크박스 */}
                                            <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${isChecked ? PURPLE : '#cdcbd8'}`, background: isChecked ? PURPLE : 'white', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 120ms' }}>
                                                {isChecked && (
                                                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M2 6l3 3 5-5" />
                                                    </svg>
                                                )}
                                            </span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span style={{ fontSize: 13.5, fontWeight: isChecked ? 600 : 400, color: '#1f1d2e' }}>{person.userName}</span>
                                                <span style={{ fontSize: 12, color: '#8b8a99', marginLeft: 8 }}>{person.userEmail}</span>
                                            </div>
                                            {person.isAppUser && (
                                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: hexToRgba(PURPLE, .10), color: PURPLE, flexShrink: 0 }}>App User</span>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div style={{ padding: '48px 20px', textAlign: 'center', color: '#8b8a99', fontSize: 13.5 }}>
                                    {searchKeyword ? '검색 결과가 없습니다.' : '추가 가능한 대상자가 없습니다.'}
                                </div>
                            )}
                        </div>

                        {/* 푸터 */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #f0eff5', flexShrink: 0 }}>
                            <span style={{ fontSize: 12.5, color: '#8b8a99' }}>
                                {selectedPersonnel.length > 0
                                    ? <><b style={{ color: PURPLE }}>{selectedPersonnel.length}명</b> 선택됨</>
                                    : '대상자를 선택하세요'}
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={handleClosePersonnelModal} style={{ height: 36, padding: '0 16px', borderRadius: 9, border: '1.5px solid #d1d5db', background: 'white', color: '#6b7280', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    취소
                                </button>
                                <button
                                    onClick={handleAddPersonnelComplete}
                                    disabled={selectedPersonnel.length === 0}
                                    style={{ height: 36, padding: '0 18px', borderRadius: 9, border: 'none', background: selectedPersonnel.length === 0 ? '#ececf2' : `linear-gradient(135deg, ${PURPLE}, ${MINT})`, color: selectedPersonnel.length === 0 ? '#aaa9b7' : 'white', fontSize: 13, fontWeight: 600, cursor: selectedPersonnel.length === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 140ms', boxShadow: selectedPersonnel.length > 0 ? `0 4px 10px -4px ${hexToRgba(PURPLE, .55)}` : 'none' }}
                                >
                                    추가하기 {selectedPersonnel.length > 0 ? `(${selectedPersonnel.length}명)` : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
